/**
 * Reveal pacing for streamed replies.
 *
 * The reveal tracks the rate the text is actually arriving at, rather than
 * racing to empty the buffer. Draining fast looks worse, not better: it
 * paints each chunk within a frame or two of arrival and then idles until
 * the next one, so the lumps survive — smaller, but with visible gaps
 * between them. Matching the arrival rate keeps characters flowing
 * continuously instead.
 *
 * Pure arithmetic, no React: the reveal loop in useStreamedReply measures
 * the stream and moves by what comes back from here.
 */

/** Floor, so a short reply still visibly types rather than blinking in. */
export const MIN_CHARS_PER_MS = 0.02;
/**
 * Ceiling, and in practice the binding constraint — the arrival-rate and
 * backlog terms below are both clamped by it, so this alone sets the pace.
 * Deliberately under the rate a model produces at, which means the reveal
 * trails a fast reply and finishes a little after it: the cost of reading
 * along comfortably rather than being outrun. This is the knob: 0.045 is ~45 chars/sec, around 9 words
 * a second. Raising it finishes long replies sooner; lowering it reads
 * calmer but leaves you watching text you could already have read.
 */
export const MAX_CHARS_PER_MS = 0.045;
/** Headroom over the arrival rate, so the buffer trends empty, not fuller. */
export const RATE_HEADROOM = 1.2;
/**
 * Backlog relief, in characters. A backlog of this size adds one char/ms on
 * top of the arrival rate — gentle enough that ordinary jitter does not
 * trigger a sprint, firm enough that a real burst still catches up.
 */
export const BACKLOG_RELIEF = 4000;
/**
 * How long the leftover text may take once the model has finished. Keeps a
 * long reply from typing for another minute after it is fully in hand.
 */
export const TAIL_DRAIN_MS = 2500;
/**
 * How far back the reveal will look for a space to stop on. Revealing two
 * characters a frame means words assemble letter by letter, which reads as
 * jitter; stopping on word boundaries makes them arrive whole. Capped so an
 * unusually long token (a URL, a base64 blob) cannot stall the reveal
 * waiting for a space that is not coming.
 */
export const WORD_SNAP_LIMIT = 24;

/**
 * Characters per millisecond the reveal should move at right now.
 *
 * Matches the pace the model is actually producing at, plus a little
 * headroom, plus relief proportional to any backlog. The slow rate exists so
 * the reader can follow along while the model is still producing. Once the
 * stream has closed there is nothing left to keep pace with — the whole
 * reply is already here — so trickling it out would just be a wait. What
 * remains drains over about TAIL_DRAIN_MS instead.
 */
export function revealRate({
  received,
  elapsedMs,
  backlog,
  streamClosed,
}: {
  /** Everything received from the network so far, in characters. */
  received: number;
  /** Since the first byte landed. */
  elapsedMs: number;
  /** Received but not yet on screen. */
  backlog: number;
  streamClosed: boolean;
}): number {
  const elapsed = Math.max(1, elapsedMs);
  const arrivalPerMs = received / elapsed;
  const paced = Math.min(
    MAX_CHARS_PER_MS,
    Math.max(
      MIN_CHARS_PER_MS,
      arrivalPerMs * RATE_HEADROOM + backlog / BACKLOG_RELIEF
    )
  );
  return streamClosed ? Math.max(paced, backlog / TAIL_DRAIN_MS) : paced;
}

/**
 * Backs a reveal position up to the nearest word boundary, so words appear
 * whole rather than letter by letter.
 *
 * Never goes below what is already on screen — text must not un-reveal — and
 * gives up if no boundary is close, so a long unbroken token still types.
 * Once the stream has closed the tail is revealed as-is, otherwise the last
 * word of a reply would sit unshown waiting for a space that never comes.
 */
export function snapToWord(
  buffer: string,
  target: number,
  current: number,
  streamClosed: boolean
): number {
  if (target >= buffer.length) return streamClosed ? target : current;
  if (/\s/.test(buffer[target])) return target;
  const boundary = buffer.lastIndexOf(" ", target);
  if (boundary <= current || target - boundary > WORD_SNAP_LIMIT) return target;
  return boundary;
}

/**
 * Where the reveal should stand after one frame of `dt` milliseconds: at
 * least one character further, never past the end of the buffer, and snapped
 * back to a word boundary.
 */
export function advanceReveal({
  buffer,
  revealed,
  dt,
  elapsedMs,
  streamClosed,
}: {
  buffer: string;
  /** How much of the buffer is on screen. */
  revealed: number;
  /** Time since the previous frame. */
  dt: number;
  /** Since the first byte landed. */
  elapsedMs: number;
  streamClosed: boolean;
}): number {
  const backlog = buffer.length - revealed;
  const perMs = revealRate({ received: buffer.length, elapsedMs, backlog, streamClosed });
  const target = Math.min(
    buffer.length,
    revealed + Math.max(1, Math.floor(dt * perMs))
  );
  return snapToWord(buffer, target, revealed, streamClosed);
}
